/**
 * Workflows retry failures.
 *
 * Operator surface for exhausted workflow retries. Reads from
 *   GET /api/v1/gateway/business2cloud/api/v1/retry-failures/grid
 *       ?organisation_id=&search=&service_name=&operation_kind=&resolved=
 *       &sort_by=&sort_order=&limit=&offset=
 *
 * The out-of-process workflow primitives (action.b2c_write,
 * action.b2c_read, action.blob_upload, action.messaging_send,
 * action.kb_file_upsert, …) wrap their external call in a retry budget.
 * When the budget is exhausted, a row lands in dp_business2cloud.RetryFailure
 * with the operation_id encoding the workflow + execution + node it came from. This page lists them —
 * the open queue by default, resolved or all on request — and lets an
 * operator mark one resolved with a note.
 *
 * Paged / searched / sorted / filtered SERVER-SIDE (usePaginatedData +
 * SearchInput + SortableTableHead + Pagination) — the page never slices,
 * sorts or filters the rows it was given. The Service / Kind Select options
 * are the response's ``facets``: DISTINCT over the org and never narrowed
 * by the current filters, so the options stay stable while filtering (D6).
 *
 * Gated to org_owner + org_admin + platform_admin via
 * usePermissions().bypassesGroups — operational action, not for org_member.
 */

import { useCallback, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  AlertCircle,
  AlertOctagonIcon,
  CheckCircle2,
  Loader2,
  Lock,
  MoreHorizontal,
  RefreshCw,
} from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useDebounce } from "@/hooks/useDebounce";
import {
  usePaginatedData,
  type PaginatedResponse,
  type ServerSortState,
} from "@/hooks/usePaginatedData";
import { usePermissions } from "@/hooks/usePermissions";
import {
  retryFailuresApi,
  type RetryFailureFacets,
  type RetryFailureResolvedFilter,
  type RetryFailureResponse,
  type RetryFailureSortField,
} from "@/services/api/workflows";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/workflows/retry-failures")({
  component: RetryFailuresPage,
});

const KIND_LABEL: Record<string, string> = {
  b2c_write: "B2C write",
  b2c_read: "B2C read",
  blob_upload: "Blob upload",
  messaging_send: "Messaging send",
  // dp action_kb_file_upsert.py `operation_kind="kb_file_upsert"`.
  kb_file_upsert: "Knowledgebase upsert",
};

const KIND_CLASS: Record<string, string> = {
  b2c_write: "bg-rose-500/10 text-rose-700 border-rose-500/30",
  b2c_read: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  blob_upload: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  messaging_send: "bg-purple-500/10 text-purple-700 border-purple-500/30",
  kb_file_upsert: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
};

const ALL = "__all__";

const EMPTY_FACETS: RetryFailureFacets = { service_names: [], operation_kinds: [] };

export function RetryFailuresPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { bypassesGroups } = usePermissions();
  const organisationId = selectedOrganisation?.organisationId ?? "";

  // Grid controls. The search term is debounced before it reaches the hook's
  // dependencies so a pause — not every keystroke — resets to page 1 and
  // refetches once (D12).
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [serviceFilter, setServiceFilter] = useState<string>(ALL);
  const [kindFilter, setKindFilter] = useState<string>(ALL);
  const [resolvedFilter, setResolvedFilter] =
    useState<RetryFailureResolvedFilter>("unresolved");
  // Select options, from the grid response (org-wide, filter-independent).
  const [facets, setFacets] = useState<RetryFailureFacets>(EMPTY_FACETS);

  const [detail, setDetail] = useState<RetryFailureResponse | null>(null);
  const [resolveTarget, setResolveTarget] = useState<RetryFailureResponse | null>(
    null,
  );
  const [resolveNote, setResolveNote] = useState("");
  const [resolveBusy, setResolveBusy] = useState(false);

  /**
   * One page of the grid, every axis server-side. Members have no access to
   * this surface (the no-access view renders instead) and the org id is
   * required by the endpoint — don't fire the call without either.
   */
  const fetchFailures = useCallback(
    async (
      page: number,
      pageSize: number,
      sort?: ServerSortState,
    ): Promise<PaginatedResponse<RetryFailureResponse>> => {
      if (!organisationId || !bypassesGroups) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const res = await retryFailuresApi.listGrid(
        organisationId,
        token ?? undefined,
        {
          search: debouncedSearch.trim() || undefined,
          serviceName: serviceFilter === ALL ? undefined : serviceFilter,
          operationKind: kindFilter === ALL ? undefined : kindFilter,
          resolved: resolvedFilter,
          sortBy: sort?.key as RetryFailureSortField | undefined,
          sortOrder: sort?.direction,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
      );
      setFacets(res.facets);
      return { data: res.rows, totalCount: res.total, page, pageSize };
    },
    [
      organisationId,
      bypassesGroups,
      token,
      debouncedSearch,
      serviceFilter,
      kindFilter,
      resolvedFilter,
    ],
  );

  const {
    data: rows,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    requestSort,
    getSortDirection,
  } = usePaginatedData<RetryFailureResponse>({
    fetchFn: fetchFailures,
    initialPageSize: 25,
    initialSort: { key: "occurred_at", direction: "desc" },
    dependencies: [
      token,
      organisationId,
      bypassesGroups,
      debouncedSearch,
      serviceFilter,
      kindFilter,
      resolvedFilter,
    ],
  });

  /**
   * Refetch after a row LEFT the grid (resolved while showing the open
   * queue). If it was this page's only row and we are past page 1, step back
   * instead of refetching the same window — the hook does not move `page`
   * when `total` shrinks, so a plain refresh would land on an empty page past
   * the end.
   */
  const refetchAfterRowLeft = useCallback(() => {
    if (rows.length === 1 && pagination.page > 1) prevPage();
    else refresh();
  }, [rows.length, pagination.page, prevPage, refresh]);

  async function confirmResolve() {
    if (!resolveTarget) return;
    const note = resolveNote.trim();
    if (!note) {
      toast.error("Resolution note is required");
      return;
    }
    setResolveBusy(true);
    try {
      await retryFailuresApi.resolve(
        resolveTarget.id,
        note,
        token ?? undefined,
      );
      toast.success("Retry failure marked resolved");
      setResolveTarget(null);
      setResolveNote("");
      // Close the detail drawer if it was showing the resolved row.
      if (detail?.id === resolveTarget.id) setDetail(null);
      // On the open queue the row leaves the grid; on "resolved"/"all" it
      // stays and just changes, so the same page is the right page.
      if (resolvedFilter === "unresolved") refetchAfterRowLeft();
      else refresh();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to resolve";
      toast.error(message);
    } finally {
      setResolveBusy(false);
    }
  }

  const hasActiveFilter =
    debouncedSearch.trim() !== "" ||
    serviceFilter !== ALL ||
    kindFilter !== ALL ||
    resolvedFilter !== "unresolved";

  if (!bypassesGroups) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Retry failures"
          description="Exhausted workflow retries that need operator attention."
          icon={AlertOctagonIcon}
          eyebrow="Operations"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center text-center gap-3 py-10">
              <Lock className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Restricted to admins</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Only org owners, org admins, and platform admins can view
                  retry failures.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Retry failures | Workflows | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Retry failures"
          description="Workflow node retries that exhausted their budget. Resolve each one with a note once the underlying problem is handled."
          icon={AlertOctagonIcon}
          eyebrow="Operations"
        />

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {/* One content panel: controls bar, then the table, then the pager.
            Every control below drives a server round-trip — nothing here
            narrows or reorders the page it was given. */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search by operation, error or service..."
              containerClassName="flex-1 min-w-[200px]"
              aria-label="Search retry failures"
              // BE = FE: the grid 422s a search over 200 chars (lara-data-plane
              // business2cloud/api/v1/retry_failures.py, list_grid); cap it
              // here so the operator never sees that 422 as an empty grid.
              maxLength={200}
            />
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-[140px]" aria-label="Service filter">
                <SelectValue className="truncate" placeholder="All services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All services</SelectItem>
                {facets.service_names.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-[140px]" aria-label="Kind filter">
                <SelectValue className="truncate" placeholder="All kinds" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All kinds</SelectItem>
                {facets.operation_kinds.map((k) => (
                  <SelectItem key={k} value={k}>
                    {KIND_LABEL[k] ?? k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={resolvedFilter}
              onValueChange={(v) =>
                setResolvedFilter(v as RetryFailureResolvedFilter)
              }
            >
              <SelectTrigger className="w-[140px]" aria-label="Status filter">
                <SelectValue className="truncate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unresolved">Unresolved</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={isLoading}
              aria-label="Refresh"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>

          {isLoading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-500" />
              <h3 className="mb-2 text-lg font-medium">
                {hasActiveFilter
                  ? "No retry failures match your filters"
                  : "Nothing to do — no unresolved retry failures"}
              </h3>
              <p className="mb-4 text-muted-foreground">
                {hasActiveFilter
                  ? "Try a different search term or filter."
                  : "Every exhausted retry has been handled."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={getSortDirection("occurred_at")}
                    onSort={() => requestSort("occurred_at")}
                  >
                    Occurred
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("service_name")}
                    onSort={() => requestSort("service_name")}
                  >
                    Service
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("operation_kind")}
                    onSort={() => requestSort("operation_kind")}
                  >
                    Kind
                  </SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>
                    Operation
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("attempt_count")}
                    onSort={() => requestSort("attempt_count")}
                  >
                    Attempts
                  </SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>
                    Error
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("resolved_at")}
                    onSort={() => requestSort("resolved_at")}
                  >
                    Resolved
                  </SortableTableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className="w-[50px]"
                  >
                    {""}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => setDetail(row)}
                  >
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatTime(row.occurred_at_utc)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.service_name}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={KIND_CLASS[row.operation_kind] ?? ""}
                      >
                        {KIND_LABEL[row.operation_kind] ?? row.operation_kind}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-[260px] truncate">
                      {row.operation_id}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.attempt_count}
                    </TableCell>
                    <TableCell className="max-w-[320px] truncate text-xs text-rose-700">
                      {row.last_error}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {row.resolved_at_utc
                        ? formatTime(row.resolved_at_utc)
                        : "—"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {/* Resolved rows have no action — the menu is not
                          rendered rather than rendered empty. */}
                      {row.resolved_at_utc ? null : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${row.operation_id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setResolveTarget(row);
                                setResolveNote("");
                              }}
                            >
                              <CheckCircle2 className="mr-2 h-4 w-4" />
                              Resolve
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {/* Outside the table/empty ternary on purpose: a page past the end
              (its last row just resolved) shows the empty state AND the
              pager, so the operator can step back. */}
          {pagination.totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={pageSizeOptions}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <SheetContent className="sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Retry failure detail</SheetTitle>
          </SheetHeader>
          {detail ? (
            <div className="space-y-4 mt-4">
              <DetailRow label="Operation id" value={detail.operation_id} mono />
              <DetailRow label="Kind" value={detail.operation_kind} />
              <DetailRow label="Service" value={detail.service_name} />
              <DetailRow label="Attempts" value={String(detail.attempt_count)} />
              <DetailRow
                label="Occurred"
                value={formatTime(detail.occurred_at_utc)}
              />
              <div>
                <Label className="text-xs text-muted-foreground">
                  Last error
                </Label>
                <pre className="mt-1 p-3 rounded bg-rose-50 dark:bg-rose-950 border border-rose-200 dark:border-rose-800 text-xs whitespace-pre-wrap break-all text-rose-900 dark:text-rose-100">
                  {detail.last_error}
                </pre>
              </div>
              {detail.context_json ? (
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Context
                  </Label>
                  <pre className="mt-1 p-3 rounded bg-muted text-xs whitespace-pre-wrap break-all">
                    {prettyJson(detail.context_json)}
                  </pre>
                </div>
              ) : null}
              {detail.resolved_at_utc ? (
                <div className="space-y-4 rounded border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950">
                  <DetailRow
                    label="Resolved"
                    value={formatTime(detail.resolved_at_utc)}
                  />
                  <DetailRow
                    label="Resolved by"
                    value={detail.resolved_by ?? "—"}
                    mono
                  />
                  <DetailRow
                    label="Resolution note"
                    value={detail.resolution_note ?? "—"}
                  />
                </div>
              ) : (
                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      setResolveTarget(detail);
                      setResolveNote("");
                    }}
                  >
                    Mark resolved…
                  </Button>
                </div>
              )}
              <ExecutionsHint operationId={detail.operation_id} />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Resolve dialog */}
      <Dialog
        open={!!resolveTarget}
        onOpenChange={(open) => {
          if (!open) {
            setResolveTarget(null);
            setResolveNote("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark retry failure resolved</DialogTitle>
            <DialogDescription>
              The row stays in the audit table — it's flagged as handled but
              never deleted. Note what you did so the next operator has
              context.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="resolution_note">Resolution note</Label>
            <Textarea
              id="resolution_note"
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              placeholder="e.g. Retried manually after Xero outage resolved; invoice posted."
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground">
              Required. Up to 1000 characters.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResolveTarget(null);
                setResolveNote("");
              }}
              disabled={resolveBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmResolve}
              disabled={resolveBusy || resolveNote.trim().length === 0}
            >
              {resolveBusy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Mark resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className={`text-sm mt-1 ${mono ? "font-mono break-all" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function ExecutionsHint({ operationId }: { operationId: string }) {
  // operation_id format: workflow:<wf>:execution:<exec>:node:<node>
  const match = operationId.match(/^workflow:[^:]+:execution:([^:]+):node:/);
  if (!match) return null;
  const executionId = match[1];
  return (
    <p className="text-xs text-muted-foreground">
      Related execution:{" "}
      <Link
        to="/portal/workflows/executions"
        className="underline hover:text-foreground"
      >
        {executionId}
      </Link>
    </p>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
